import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Notice, describeAuthError } from './Notice';
import type { NoticeTone } from './Notice';
import { validateIdentifier, validatePassword } from './validators';
import type { ValidationKey } from './validators';
import type { IdentifierValidation, Portal } from './types';
import { getLoginKitMessages } from './i18n';
import type { Locale } from './i18n';

export interface LoginCredentials {
  identifier: string;
  password: string;
}

export interface StandardFormProps {
  portal: Portal;
  /** Idioma da interface (pt-BR, en, es). */
  locale?: Locale;
  /** Handler de login e-mail+senha. Se ausente, o envio é simulado. */
  onSubmit?: (creds: LoginCredentials) => Promise<void> | void;
  /** Handler de link mágico. Se ausente, o envio é simulado. */
  /**
   * Envia o link mágico. `onOutcome` reporta o que aconteceu depois do envio — o clique só chega minutos
   * depois, e um link aberto no navegador errado precisa tirar a tela do estado "enviado".
   */
  onMagicLink?: (identifier: string, onOutcome?: (error: Error) => void) => Promise<void> | void;
  /** Abre o fluxo de recuperação de senha. */
  onForgot?: () => void;
  /**
   * Aviso já visível quando a tela abre. Existe para quem chega aqui vindo de outro lugar com algo a
   * dizer — um link mágico aberto no navegador errado devolve a pessoa ao login, e sem isto ela não
   * recebe explicação nenhuma. Dispensável como qualquer outro erro, e o formulário segue utilizável.
   */
  initialNotice?: { tone: NoticeTone; message: string } | null;
}

type Status = 'idle' | 'loading' | 'done' | 'sent';
type FieldErrors = { id?: ValidationKey; pw?: ValidationKey };
type Touched = { id?: boolean; pw?: boolean };

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * O formulário padrão — estrutura idêntica em todos os portais. Esse é o padrão.
 *
 * Métodos de acesso: e-mail + senha e link mágico (passwordless). Os provedores
 * de identidade externos (Google/Apple/Microsoft) foram removidos por ora — só
 * voltam quando o produto os habilitar de novo.
 *
 * Feedback: erros de envio (antes engolidos) viram um banner `Notice` com
 * mensagem amigável mapeada por situação; o sucesso do login é confirmado antes
 * do redirecionamento. Validação client-side bloqueia o envio e marca os campos
 * inválidos. Tudo internacionalizado (pt-BR/en/es).
 */
export function StandardForm({
  portal,
  locale = 'pt-BR',
  onSubmit,
  onMagicLink,
  onForgot,
  initialNotice = null,
}: StandardFormProps) {
  const [magic, setMagic] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [id, setId] = useState('');
  const [pw, setPw] = useState('');
  const [notice, setNotice] = useState<{ tone: NoticeTone; message: string } | null>(initialNotice);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Touched>({});
  const t = getLoginKitMessages(locale);

  const minLen = portal.passwordMinLength ?? 6;
  const idKind: IdentifierValidation =
    portal.identifier.validation ?? (portal.identifier.type === 'email' ? 'email' : 'text');

  // Resolve uma chave de validação na mensagem traduzida (interpola {min}).
  const messageFor = (key?: ValidationKey): string | undefined => {
    if (!key) return undefined;
    if (key === 'passwordMin') return t.validation.passwordMin.replace('{min}', String(minLen));
    return t.validation[key];
  };

  const checkId = (value: string) => validateIdentifier(value, idKind) ?? undefined;
  const checkPw = (value: string) => (magic ? undefined : validatePassword(value, minLen) ?? undefined);

  // Reseta estado transitório quando o portal muda — de verdade, não na montagem também. Effects rodam
  // na primeira renderização, então isto limpava o `initialNotice` no instante em que a tela abria: a
  // mensagem existia, chegava até aqui e era apagada antes de alguém ver.
  const mountedPortal = useRef(portal.id);
  useEffect(() => {
    if (mountedPortal.current === portal.id) return;
    mountedPortal.current = portal.id;

    setStatus('idle');
    setMagic(false);
    setNotice(null);
    setErrors({});
    setTouched({});
  }, [portal.id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;

    // 1) Validação client-side — bloqueia o envio e marca os campos.
    const idErr = checkId(id);
    const pwErr = checkPw(pw);
    setErrors({ id: idErr, pw: pwErr });
    setTouched({ id: true, pw: true });
    if (idErr || pwErr) {
      setNotice(null);
      return;
    }

    // 2) Envio.
    setNotice(null);
    setStatus('loading');
    try {
      if (magic) {
        // O retorno vem de um polling que continua depois deste `await`: trazer a pessoa de volta ao
        // formulário é o que permite pedir outro link, em vez de encarar "Link enviado" para sempre.
        if (onMagicLink) {
          await onMagicLink(id, (error) => {
            setStatus('idle');
            setNotice({ tone: 'error', message: describeAuthError(error, t.notice, t.notice.magicLinkError) });
          });
        } else {
          await wait(1300);
        }
        setStatus('sent');
      } else {
        if (onSubmit) await onSubmit({ identifier: id, password: pw });
        else await wait(1300);
        setStatus('done');
        setNotice({ tone: 'success', message: t.notice.loginSuccess });
      }
    } catch (error) {
      setStatus('idle');
      setNotice({
        tone: 'error',
        message: describeAuthError(error, t.notice, magic ? t.notice.magicLinkError : t.notice.loginError),
      });
    }
  };

  if (status === 'sent') {
    return (
      <div className="form-confirm" key="sent">
        <span className="confirm-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M3 8l4 3 6-8" transform="translate(3 5)" />
          </svg>
        </span>
        <h2 className="confirm-title">{t.form.sentTitle}</h2>
        <p className="confirm-text">
          {t.form.sentPrefix} <strong>{id || t.form.sentFallbackEmail}</strong>. {t.form.sentSuffix}
        </p>
        <button
          className="ghost-btn"
          onClick={() => {
            setStatus('idle');
            setMagic(false);
          }}
        >
          {t.form.backToLogin}
        </button>
      </div>
    );
  }

  const chipStyle = { '--accent': portal.accent, '--accent-soft': portal.accentSoft } as CSSProperties;
  const footLinkStyle = { '--accent': portal.accent } as CSSProperties;

  const idError = touched.id ? messageFor(errors.id) : undefined;
  const pwError = touched.pw ? messageFor(errors.pw) : undefined;

  return (
    <form className="login-form" onSubmit={submit} key="form" noValidate>
      <div className="form-chip" style={chipStyle}>
        <span className="chip-dot" />
        {portal.name}
      </div>

      <h2 className="form-title">{t.form.title}</h2>
      <p className="form-lead">{t.form.lead}</p>

      {notice && (
        <Notice
          tone={notice.tone}
          title={notice.tone === 'error' ? t.notice.errorTitle : notice.tone === 'success' ? t.notice.successTitle : undefined}
          onDismiss={notice.tone === 'error' ? () => setNotice(null) : undefined}
          dismissLabel={t.notice.dismiss}
        >
          {notice.message}
        </Notice>
      )}

      <label className="field">
        <span className="field-label">{portal.identifier.label}</span>
        <input
          id="login-identifier"
          className="field-input"
          type={portal.identifier.type}
          placeholder={portal.identifier.placeholder}
          autoComplete={portal.identifier.autocomplete}
          value={id}
          aria-invalid={idError ? true : undefined}
          aria-describedby={idError ? 'login-identifier-error' : undefined}
          onChange={(e) => {
            const value = e.target.value;
            setId(value);
            if (touched.id) setErrors((prev) => ({ ...prev, id: checkId(value) }));
          }}
          onBlur={(e) => {
            setTouched((prev) => ({ ...prev, id: true }));
            setErrors((prev) => ({ ...prev, id: checkId(e.target.value) }));
          }}
        />
        {idError && (
          <span className="field-error" id="login-identifier-error" role="alert">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5v5" />
              <path d="M12 16h.01" />
            </svg>
            {idError}
          </span>
        )}
      </label>

      {!magic && (
        <label className="field">
          <span className="field-label-row">
            <span className="field-label">{t.form.password}</span>
            <a
              href="#"
              className="field-link"
              onClick={(e) => {
                e.preventDefault();
                onForgot?.();
              }}
            >
              {t.form.forgotPassword}
            </a>
          </span>
          <span className="field-pw">
            <input
              id="login-password"
              className="field-input"
              type={showPw ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="current-password"
              value={pw}
              aria-invalid={pwError ? true : undefined}
              aria-describedby={pwError ? 'login-password-error' : undefined}
              onChange={(e) => {
                const value = e.target.value;
                setPw(value);
                if (touched.pw) setErrors((prev) => ({ ...prev, pw: checkPw(value) }));
              }}
              onBlur={(e) => {
                setTouched((prev) => ({ ...prev, pw: true }));
                setErrors((prev) => ({ ...prev, pw: checkPw(e.target.value) }));
              }}
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? t.form.hidePassword : t.form.showPassword}
            >
              {showPw ? t.form.hidePassword : t.form.showPassword}
            </button>
          </span>
          {pwError && (
            <span className="field-error" id="login-password-error" role="alert">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7.5v5" />
                <path d="M12 16h.01" />
              </svg>
              {pwError}
            </span>
          )}
        </label>
      )}

      {portal.secure && (
        <div className="secure-note">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2l7 3v6c0 4.4-3 8.5-7 9.7-4-1.2-7-5.3-7-9.7V5l7-3z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          {t.form.secure2fa}
        </div>
      )}

      <button
        type="submit"
        className={`primary-btn ${status === 'loading' ? 'is-loading' : ''} ${status === 'done' ? 'is-done' : ''}`}
      >
        <span className="btn-label">
          {status === 'loading'
            ? t.form.loggingIn
            : status === 'done'
              ? t.form.done
              : magic
                ? t.form.magicLink
                : t.form.login}
        </span>
      </button>

      <button
        type="button"
        className="magic-btn"
        onClick={() => {
          setMagic((v) => !v);
          setStatus('idle');
          setNotice(null);
          setErrors((prev) => ({ ...prev, pw: undefined }));
          setTouched((prev) => ({ ...prev, pw: false }));
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 8l8 5 8-5" />
          <rect x="4" y="5" width="16" height="14" rx="2" />
        </svg>
        {magic ? t.form.backToPassword : t.form.magicLink}
      </button>

      {portal.createLabel && (
        <p className="form-foot">
          <a href="#" className="foot-link" style={footLinkStyle} onClick={(e) => e.preventDefault()}>
            {portal.createLabel}
          </a>
        </p>
      )}
    </form>
  );
}
